import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../website/layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_592e95a9 from 'nuxt_plugin_sentryserver_592e95a9' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_1915b7be from 'nuxt_plugin_sentryclient_1915b7be' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_toast_664050d8 from 'nuxt_plugin_toast_664050d8' // Source: ./toast.js (mode: 'client')
import nuxt_plugin_pluginrouting_0e2951c1 from 'nuxt_plugin_pluginrouting_0e2951c1' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_4b01d94e from 'nuxt_plugin_pluginmain_4b01d94e' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_apollomodule_fa00bc9e from 'nuxt_plugin_apollomodule_fa00bc9e' // Source: ./apollo-module.js (mode: 'all')
import nuxt_plugin_axios_00af8ae5 from 'nuxt_plugin_axios_00af8ae5' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_cookieuniversalnuxt_1d6e7ee6 from 'nuxt_plugin_cookieuniversalnuxt_1d6e7ee6' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_notifications_1abb4b52 from 'nuxt_plugin_notifications_1abb4b52' // Source: ../plugins/notifications.js (mode: 'client')
import nuxt_plugin_veevalidate_d1fbfbbc from 'nuxt_plugin_veevalidate_d1fbfbbc' // Source: ../plugins/vee-validate.js (mode: 'all')
import nuxt_plugin_i18n_3127b3d1 from 'nuxt_plugin_i18n_3127b3d1' // Source: ../plugins/i18n.js (mode: 'all')
import nuxt_plugin_simlawebframework_035ac856 from 'nuxt_plugin_simlawebframework_035ac856' // Source: ../plugins/simlaweb-framework.js (mode: 'all')
import nuxt_plugin_trackerclient_19f413e0 from 'nuxt_plugin_trackerclient_19f413e0' // Source: ../website/plugins/tracker.client.js (mode: 'client')
import nuxt_plugin_omnica_22d9515a from 'nuxt_plugin_omnica_22d9515a' // Source: ../website/plugins/omnica.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    return this.$root.$options.$nuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"name":"description","content":"","hid":"description"}],"link":[{"hid":"favicon","rel":"icon","type":"image\u002Fpng","href":"\u002Ffavicon.png"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_592e95a9 === 'function') {
    await nuxt_plugin_sentryserver_592e95a9(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_1915b7be === 'function') {
    await nuxt_plugin_sentryclient_1915b7be(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_toast_664050d8 === 'function') {
    await nuxt_plugin_toast_664050d8(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_0e2951c1 === 'function') {
    await nuxt_plugin_pluginrouting_0e2951c1(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_4b01d94e === 'function') {
    await nuxt_plugin_pluginmain_4b01d94e(app.context, inject)
  }

  if (typeof nuxt_plugin_apollomodule_fa00bc9e === 'function') {
    await nuxt_plugin_apollomodule_fa00bc9e(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_00af8ae5 === 'function') {
    await nuxt_plugin_axios_00af8ae5(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_1d6e7ee6 === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_1d6e7ee6(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_notifications_1abb4b52 === 'function') {
    await nuxt_plugin_notifications_1abb4b52(app.context, inject)
  }

  if (typeof nuxt_plugin_veevalidate_d1fbfbbc === 'function') {
    await nuxt_plugin_veevalidate_d1fbfbbc(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_3127b3d1 === 'function') {
    await nuxt_plugin_i18n_3127b3d1(app.context, inject)
  }

  if (typeof nuxt_plugin_simlawebframework_035ac856 === 'function') {
    await nuxt_plugin_simlawebframework_035ac856(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_trackerclient_19f413e0 === 'function') {
    await nuxt_plugin_trackerclient_19f413e0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_omnica_22d9515a === 'function') {
    await nuxt_plugin_omnica_22d9515a(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, (err) => {
        // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
        if (!err._isRouter) return reject(err)
        if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
